import { BrowserRouter } from 'react-router-dom';
import Routes from './Routes';
import Page from './components/Page';
import { HelmetProvider } from 'react-helmet-async';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import ErrorBoundary from './components/ErrorBoundary';
import SiteEntities from './components/SiteEntities';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { UserProvider } from './contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { SeasonProvider } from './config/seasonContext';
import ScrollToTop from './components/ScrollToTop';
import { useEffect } from 'react';
import { identifyUser } from './services/posthog';
import { useSessionRecording } from './hooks/useSessionRecording';
import { useAnalytics } from './hooks/useAnalytics';
import { classifyAuthEvent } from './utils/auth';
import { captureRefFromUrl } from './utils/referral';
import { isPrerendering } from './utils/detectPrerender';
import { trackStartLead } from './utils/adPixels';

// Analytics wrapper to handle PostHog initialization and user identification
export const AnalyticsWrapper = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuth0();
  const { trackSignup, trackLogin } = useAnalytics();

  // $pageview, landing and route changes alike, is captured by the PostHog
  // snippet in public/index.html (capture_pageview: 'history_change').
  // Session replay on RECORDED_ROUTES only (services/posthog).
  useSessionRecording();

  useEffect(() => {
    // Skip analytics during pre-rendering
    if (isPrerendering()) return;
    // Capture a ?ref=<code> referral param on first load; persists to checkout.
    captureRefFromUrl(window.location.search);
  }, []);

  useEffect(() => {
    // Skip during pre-rendering
    if (isPrerendering()) return;
    // Identify user when authentication state changes
    if (!isLoading && isAuthenticated && user) {
      identifyUser(user.sub, {
        email: user.email,
        name: user.name,
        given_name: user.given_name,
        family_name: user.family_name,
        email_verified: user.email_verified,
        signup_method: 'auth0'
      });

      // Fire a one-time signup/login event when a real auth action just completed
      // (flagged in onRedirectCallback). classifyAuthEvent distinguishes a first-time
      // signup from a returning login. Cached-session page loads don't set the flag,
      // so they don't fire a spurious event.
      let authAction = false;
      try { authAction = sessionStorage.getItem('pev_auth_action') === '1'; } catch (e) {}
      if (authAction) {
        try { sessionStorage.removeItem('pev_auth_action'); } catch (e) {}
        const kind = classifyAuthEvent(user.sub);
        const props = { email_verified: user.email_verified };
        if (kind === 'signup') {
          trackSignup('auth0', props);
          // #315: fire the X Lead pixel on a first-time signup, once per browser
          // session. classifyAuthEvent is per-device, so an existing user on a new
          // device counts as 'signup' (accepted by the founder). Set the guard key
          // before firing, and if storage throws, skip rather than risk double-firing.
          try {
            if (sessionStorage.getItem('pev_x_signup_lead') !== '1') {
              sessionStorage.setItem('pev_x_signup_lead', '1');
              trackStartLead();
            }
          } catch (e) {}
        } else if (kind === 'login') trackLogin('auth0', props);
      }
    }
  }, [isAuthenticated, user, isLoading, trackSignup, trackLogin]);

  return children;
};

const Auth0ProviderWithRedirect = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    // Flag that a real auth action (login/signup) just completed. The AnalyticsWrapper
    // effect reads this once user details load and fires user_signup/user_login. This
    // callback runs only on an actual redirect return, never on cached-session loads.
    try { sessionStorage.setItem('pev_auth_action', '1'); } catch (e) {}
    navigate(appState?.returnTo || '/edgeboard', { replace: true });
  };

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/auth0-return`,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access'
      }}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      <AnalyticsWrapper>
        {children}
      </AnalyticsWrapper>
    </Auth0Provider>
  );
};

const App = () => {
  // console.log('Auth0 Config:', {
  //   domain: process.env.REACT_APP_AUTH0_DOMAIN,
  //   clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
  //   audience: process.env.REACT_APP_AUTH0_AUDIENCE
  // });

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Auth0ProviderWithRedirect>
        <UserProvider>
          <HelmetProvider>
            <SiteEntities />
            <SubscriptionProvider>
              <SeasonProvider>
                <Page>
                  <ErrorBoundary>
                    <Routes />
                  </ErrorBoundary>
                </Page>
              </SeasonProvider>
            </SubscriptionProvider>
          </HelmetProvider>
        </UserProvider>
      </Auth0ProviderWithRedirect>
    </BrowserRouter>
  );
};

export default App;
